import { useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { XMarkIcon } from "@heroicons/react/20/solid";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import type { ProductHitWithSwatches } from "~/lib/app.types";
import { cn } from "~/lib/ui";
import {
  clearRefinements,
  getRefinementsObjectFromString,
} from "~/shop/utils/refinements";

import { useProductListHistoryState } from "../location-state/product-list.location-state";

/**
 * Product List Hooks (Salesforce)
 *
 * Encapsulates state management and URL interactions for the product list route.
 *
 * - Uses React Router 7 `useSearchParams` to keep filters/sort in the URL
 * - Utilizes refs to share latest totals/hits across suspense boundaries without
 *   forcing re-renders of pre-rendered tiles
 */

// Custom hooks for better organization
/**
 * Manages UI state for product list controls (filters and sorting dialogs).
 *
 * @returns Object with state and setters for filter/sort dialogs and opened filter id
 */
export const useProductListFilters = () => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortingOpen, setSortingOpen] = useState(false);
  const [openFilterId, setOpenFilterId] = useState<string | undefined>();

  const onFilterOpen = useCallback((filterId: string | undefined) => {
    setFiltersOpen(true);
    setOpenFilterId(filterId);
  }, []);

  return {
    filtersOpen,
    setFiltersOpen,
    sortingOpen,
    setSortingOpen,
    openFilterId,
    setOpenFilterId,
    onFilterOpen,
  };
};

/**
 * Provides product list route data wiring: search params, derived refinements, and refs.
 *
 * @returns Object containing: state, searchParams/setSearchParams, totalProductsRef,
 *          allHitsRef, selectedRefinements, numberOfFiltersApplied, query
 */
export const useProductListData = () => {
  const [state] = useProductListHistoryState();
  const [searchParams, setSearchParams] = useSearchParams();

  // Refs to share data between predata and new data without re-rendering predata tiles
  const totalProductsRef = useRef<number>(state?.predata?.total ?? 0);
  const allHitsRef = useRef<ProductHitWithSwatches[]>(
    state?.predata?.hits ?? [],
  );

  const selectedRefinements = useMemo(
    () => getRefinementsObjectFromString(searchParams.get("refine") ?? ""),
    [searchParams],
  );

  const numberOfFiltersApplied = useMemo(
    () => Object.values(selectedRefinements).flat().length,
    [selectedRefinements],
  );

  const query = searchParams.get("q");

  return {
    state,
    searchParams,
    setSearchParams,
    totalProductsRef,
    allHitsRef,
    selectedRefinements,
    numberOfFiltersApplied,
    query,
  };
};

/**
 * Returns a renderer for a "Clear filters" button.
 *
 * URL effects:
 * - Clears `refine`
 * - Removes `page` and optionally `cursor` (when includeCursor is true)
 * - Uses `preventScrollReset` to avoid unwanted scroll jumps
 */
export const useClearFilters = (
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  includeCursor = false,
) => {
  return useCallback(
    (className?: string) => {
      return (
        <button
          onClick={() => {
            setSearchParams(
              prev => {
                prev.set("refine", clearRefinements(prev.get("refine") ?? ""));
                prev.delete("page");
                if (includeCursor) {
                  prev.delete("cursor");
                }
                return prev;
              },
              {
                preventScrollReset: true,
              },
            );
          }}
          className={cn(
            "flex items-center justify-center rounded bg-transparent px-2 py-2 text-base font-light text-accentalpha-11 underline underline-offset-2",
            className,
          )}
        >
          Clear filters
        </button>
      );
    },
    [setSearchParams, includeCursor],
  );
};

/**
 * Returns a renderer for the "Sort by" button with removable active sort badge.
 *
 * URL effects:
 * - Opens sorting dialog
 * - Badge X click removes `sort`, resets `page`, and optionally removes `cursor`
 */
export const useSortButton = (
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  setSortingOpen: (open: boolean) => void,
  includeCursor = false,
) => {
  return useCallback(
    (searchParams: URLSearchParams, className?: string) => {
      return (
        <Button
          type="button"
          className={cn("text-base font-semibold text-neutral-11", className)}
          variant="outline"
          onClick={() => setSortingOpen(true)}
        >
          Sort by
          {searchParams.get("sort") && (
            <Badge
              variant="secondary"
              key={searchParams.get("sort")}
              className="mx-1.5 rounded-sm text-base font-semibold text-neutral-11"
            >
              {searchParams.get("sort")}
              <Button
                variant="linkInline"
                className="h-[18px] w-[18px] bg-transparent text-neutral-9"
                onClick={(e: { stopPropagation: () => void }) => {
                  setSearchParams(
                    prev => {
                      prev.delete("sort");
                      prev.delete("page");
                      if (includeCursor) {
                        prev.delete("cursor");
                      }
                      return prev;
                    },
                    { preventScrollReset: true },
                  );
                  e.stopPropagation();
                }}
              >
                <XMarkIcon />
              </Button>
            </Badge>
          )}
        </Button>
      );
    },
    [setSearchParams, setSortingOpen, includeCursor],
  );
};

/**
 * Provides a click handler to clear applied filters in small inline controls.
 *
 * URL effects:
 * - Clears `refine`, resets `page`, and optionally removes `cursor`
 */
export const useFilterButtonClick = (
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  includeCursor = false,
) => {
  return useCallback(
    (e: { stopPropagation: () => void }) => {
      setSearchParams(
        prev => {
          prev.set("refine", clearRefinements(prev.get("refine") ?? ""));
          prev.delete("page");
          if (includeCursor) {
            prev.delete("cursor");
          }
          return prev;
        },
        { preventScrollReset: true },
      );
      e.stopPropagation();
    },
    [setSearchParams, includeCursor],
  );
};
